import * as React from 'react'
import { FormField } from '@/components/templates/entity-form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { MembershipLevelForm, MembershipLevelKind } from '@/pages/MembershipLevelsPage.types'

export function MembershipLevelBasics({
  form,
  mode,
  errors,
  onChange,
}: {
  form: MembershipLevelForm
  mode: 'new' | 'edit'
  errors: { name?: string; kind?: string; rosterCap?: string; fee?: string }
  onChange: (form: MembershipLevelForm) => void
}) {
  const organization = form.kind === 'organization'

  return (
    <section className="flex flex-col gap-4 pb-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Name" htmlFor="membership-level-name" error={errors.name}>
          <Input
            id="membership-level-name"
            value={form.name}
            placeholder="e.g. Individual"
            onChange={(event) => onChange({ ...form, name: event.target.value })}
          />
        </FormField>
        <FormField label="Annual fee" htmlFor="membership-level-fee" error={errors.fee}>
          <Input
            id="membership-level-fee"
            inputMode="decimal"
            value={form.fee}
            placeholder="0.00"
            onChange={(event) => onChange({ ...form, fee: event.target.value })}
          />
        </FormField>
      </div>
      <FormField label="Description" htmlFor="membership-level-description">
        <Textarea
          id="membership-level-description"
          rows={2}
          value={form.description}
          onChange={(event) => onChange({ ...form, description: event.target.value })}
        />
      </FormField>
      <MembershipLevelKindField
        mode={mode}
        kind={form.kind}
        error={errors.kind}
        onChange={(kind) => onChange({ ...form, kind })}
      />
      {organization && (
        <FormField
          label="Roster cap"
          htmlFor="membership-level-roster-cap"
          hint="Leave empty for an unlimited Roster."
          error={errors.rosterCap}
        >
          <Input
            id="membership-level-roster-cap"
            inputMode="numeric"
            value={form.rosterCap}
            placeholder="Unlimited"
            onChange={(event) => onChange({ ...form, rosterCap: event.target.value })}
          />
        </FormField>
      )}
    </section>
  )
}

function MembershipLevelKindField({
  mode,
  kind,
  error,
  onChange,
}: {
  mode: 'new' | 'edit'
  kind: MembershipLevelKind
  error?: string
  onChange: (kind: MembershipLevelKind) => void
}) {
  if (mode === 'edit') {
    return (
      <FormField
        label="Kind"
        hint="Kind is fixed at creation; create a new level to change shape."
        error={error}
      >
        <p className="text-sm text-foreground" data-testid="membership-level-kind-readonly">
          {kind === 'organization' ? 'Organization' : 'Individual'}
        </p>
      </FormField>
    )
  }

  return (
    <FormField label="Kind" htmlFor="membership-level-kind" error={error}>
      <Select value={kind} onValueChange={(value) => onChange(value as MembershipLevelKind)}>
        <SelectTrigger id="membership-level-kind" aria-label="Kind">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="individual">Individual</SelectItem>
          <SelectItem value="organization">Organization</SelectItem>
        </SelectContent>
      </Select>
    </FormField>
  )
}
